import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, finalize, of, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  RemittanceWorksheet,
  RemittanceWorksheetRow,
  RemittanceSummary,
  WithholdingRemittance,
  WithholdingRemittanceCharge,
  WithholdingRemittanceStatus,
} from '../../../models/withholding-remittance/withholding-remittance';
import { WithholdingRemittanceChargeFormGroup } from '../../../models/withholding-remittance/withholding-remittance-form';
import { PostingModule } from '../../../models/financial-account/financial-account';
import { WithholdingRemittanceProxyService } from '../../../proxies/withholding-remittance.proxy.service';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import { CashRequestProxyService } from '../../../proxies/cash-request.proxy.service';
import { BankAccount } from '../../../models/bank-account/bank-account';
import { Currency } from '../../../models/ap-document/ap-document';
import { exchangeRateBoundsFor, isPlausibleExchangeRate } from '../../../utilities/exchange-rate-bounds';
import { CashRequestStatus } from '../../../models/cash-request/cash-request';
import { EnumService } from '../../../services/enum.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { SiteService } from '@card-software/library';
import { RATE_DECIMALS } from '../../../utilities/settlement-rate.util';

interface Option<T> {
  label: string;
  value: T;
}

/**
 * Monthly withholding remittance to SAR (#4489). Pick a period → the worksheet shows every withholdable AP
 * document posted that month with its retention status ("here's what you're about to pay") and flags docs
 * with no certificate (a soft warning). Prepare the draft, pick the bank account, and post — which pays SAR
 * (Dr Retenciones por Pagar / Cr Bank) and marks each retention remitted.
 */
@Component({
  selector: 'app-withholding-remittance',
  templateUrl: './withholding-remittance.component.html',
  styleUrl: './withholding-remittance.component.scss',
})
export class WithholdingRemittanceComponent implements OnInit {
  /** Rate precision for the rate inputs — the API's (CurrencyConverter.RateDecimals, #5359). */
  protected readonly rateDecimals = RATE_DECIMALS;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(WithholdingRemittanceProxyService);
  private readonly bankService = inject(BankAccountProxyService);
  private readonly cashRequestService = inject(CashRequestProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);

  isLoading = signal(0);
  headerTitle = signal<string>(this.t('WithholdingRemittance_Title', 'Withholding Remittance'));

  readonly year = signal<number>(0);
  readonly month = signal<number>(0);
  readonly worksheet = signal<RemittanceWorksheet | null>(null);
  readonly draft = signal<WithholdingRemittance | null>(null);
  readonly bankOptions = signal<Option<string>[]>([]);

  readonly bankControl = new FormControl<string | null>(null);
  readonly bankingRefControl = new FormControl<string | null>(null);

  /** The accounts behind {@link bankOptions}, kept whole because the currency decides whether the bank
   *  converts on the way out (#5029) — the option's value is only an id. */
  private readonly bankAccounts = signal<BankAccount[]>([]);

  /** Which account is chosen, as a signal. The control is the source of truth; this mirrors it so the
   *  conversion readouts below can be computed rather than recalculated by hand on every change. */
  private readonly selectedBankId = signal<string | null>(null);

  /** The rate the bank gave for the wire (#5029) — required only when the chosen account holds a different
   *  money than the filing owes, and never prefilled from the day's table: the table records an average and
   *  the bank charges this wire's spread, so a prefilled figure invites approving a number nobody was
   *  quoted. Same rule as an AP payment's wire quote (#5135). */
  readonly rateControl = new FormControl<number | null>(null);
  readonly notesControl = new FormControl<string | null>(null);

  /** Which week's cash pays SAR (#4796). Optional — a tag, never a gate on a statutory filing. */
  readonly cashRequestControl = new FormControl<string | null>(null);
  readonly cashRequestOptions = signal<Option<string>[]>([]);

  readonly monthOptions: Option<number>[] = [];
  readonly yearOptions: Option<number>[] = [];
  private currencyLabels: Record<number, string> = {};

  /** Charges SAR added on top (#5029) — a FormArray because the officer adds and removes rows. */
  readonly charges = new FormArray<FormGroup<WithholdingRemittanceChargeFormGroup>>([]);

  /** Which module the charge accounts must authorize — the same one the remittance's entry posts from. */
  readonly PostingModule = PostingModule;

  /** Recomputed from the form rather than read off the draft, so the totals move as the officer types
   *  instead of only after a save. Mirrors the API's ChargesAmount / TotalPaid exactly. */
  readonly chargesTotal = signal(0);

  // ── Funding the filing (#5029) ────────────────────────────────────────────
  //
  // The remittance is a Lempira liability to SAR and stays one — SAR is owed Lempiras whatever we pay from.
  // A company holding dollars funds it from a dollar account and the bank converts on the way out, so the
  // officer states what the bank charged and the screen shows what will actually leave.

  /** The money the filing owes. HNL for every remittance today; read from the draft rather than assumed. */
  readonly remittanceCurrency = computed<Currency>(() => this.draft()?.currency ?? Currency.HNL);

  /** The money the chosen account holds, or null while none is chosen. */
  readonly fundingCurrency = computed<Currency | null>(() => {
    const id = this.selectedBankId();
    if (!id) return null;
    return this.bankAccounts().find((account) => account.id === id)?.currency ?? null;
  });

  /** True when the bank converts: the account holds a different money than the filing owes. */
  readonly converts = computed<boolean>(() => {
    const funding = this.fundingCurrency();
    return funding !== null && funding !== this.remittanceCurrency();
  });

  /** The side the quote is stated in — the non-USD one, since every pair routes through the dollar. */
  readonly quoteCurrency = computed<Currency | null>(() => {
    if (!this.converts()) return null;
    return this.remittanceCurrency() !== Currency.USD ? this.remittanceCurrency() : this.fundingCurrency();
  });

  /** What is typed into the rate field right now — a signal so the readout prices as it is typed rather
   *  than only once the field is left (PrimeNG's number input commits on blur). */
  private readonly typedRate = signal<number | null>(null);

  /** What will leave the funding account, at the rate as typed. Zero until there is a usable rate, which
   *  is the honest answer: the cash cannot be known before the bank says what it charged. */
  readonly cashOut = computed<number>(() => {
    const rate = this.typedRate();
    if (!this.converts() || !rate || rate <= 0) return 0;
    // The quote is units of the non-USD side per USD, so one side of the pair is 1 by definition.
    const perUsd = (currency: Currency | null) => (currency === Currency.USD ? 1 : rate);
    const wireRate = perUsd(this.fundingCurrency()) / perUsd(this.remittanceCurrency());
    return Math.round(this.totalPaid() * wireRate * 100) / 100;
  });

  /** True when the typed rate is one this currency plausibly trades at — a slipped decimal is caught here
   *  as well as by the API, so it is caught while the officer is still looking at the field. */
  readonly rateOutOfBounds = computed<boolean>(() => !isPlausibleExchangeRate(this.quoteCurrency(), this.typedRate()));

  readonly rateBoundsHint = computed<string | null>(() => {
    const bounds = exchangeRateBoundsFor(this.quoteCurrency());
    return bounds ? `${bounds.min} – ${bounds.max}` : null;
  });

  readonly hasWorksheet = computed(() => !!this.worksheet());
  readonly totalToRemit = computed(() => this.worksheet()?.totalToRemit ?? 0);
  readonly missingCount = computed(() => this.worksheet()?.missingWithholdingCount ?? 0);
  readonly hasDraft = computed(() => !!this.draft());

  /**
   * Whether another remittance may be opened for this period (#5029). The API's answer, not a derivation
   * from `existingRemittanceReference` — how many filings a period may carry is a deployment policy, so a
   * screen that worked it out locally would be wrong the moment the config changed.
   */
  readonly canRemit = computed(() => this.worksheet()?.canRemit ?? false);

  /** True while the PERIOD has a draft, whoever created it — as opposed to `hasDraft`, which only knows
   *  about one this page loaded. The two used to be conflated, and a period with an untouched draft
   *  reported itself as already remitted. */
  readonly periodHasOpenDraft = computed(
    () => this.worksheet()?.remittances?.some((r) => r.status === WithholdingRemittanceStatus.Draft) ?? false,
  );

  /**
   * A filed period the deployment will not let us file again — the message, not the button.
   *
   * `canRemit` is false for two unrelated reasons (a draft is open, or the period is filed and the policy
   * allows one filing), so it cannot be rendered as a message on its own. Only the second is "already
   * remitted"; the first means there is work in progress to finish.
   */
  readonly periodClosed = computed(() => this.hasWorksheet() && !this.canRemit() && !this.periodHasOpenDraft() && !this.hasDraft());

  // ── what has already been filed for this period (#5029) ─────────────────────

  /** Every filing made for the period, newest first — the officer reads the most recent one most often. */
  readonly remittances = computed(() => [...(this.worksheet()?.remittances ?? [])].reverse());

  /** Only POSTED filings are money that actually left; a draft in the list is work in progress. */
  readonly postedRemittances = computed(() => this.remittances().filter((r) => r.status === WithholdingRemittanceStatus.Posted));

  /** What this period has cost so far, across every filing. A late top-up is meaningless without it: the
   *  officer needs "August has now cost X in total", not just "this top-up was Y". */
  readonly totalRemittedForPeriod = computed(() => this.postedRemittances().reduce((sum, r) => sum + r.totalPaid, 0));

  /** The filing open in the read-only viewer, fetched in full when a row is clicked. */
  readonly viewing = signal<WithholdingRemittance | null>(null);
  readonly viewerOpen = signal(false);

  readonly canPrepare = computed(() => this.hasWorksheet() && this.canRemit() && this.totalToRemit() > 0);

  /**
   * Whether this period has been filed before (#5029) — which makes whatever is prepared next a
   * SUPPLEMENTARY filing rather than the month's first.
   *
   * <p>The screen has always been able to open one: `prepare()` is the same call, and the API answers
   * `canRemit` and numbers the result WHR-YYYY-MM-2. What it never did was SAY so — the button read
   * "Prepare remittance" exactly as it did for the first, and a period with nothing new left said only
   * "Nothing to remit", which reads as "this month is closed" rather than "there is nothing to add YET".
   * An officer who has just filed August and is told to expect a late certificate has no way to know the
   * screen will let them file again.</p>
   */
  readonly hasFiledThisPeriod = computed(() => this.postedRemittances().length > 0);

  /** True when the period is filed, another may be opened, and nothing new has been certified yet — the
   *  state an officer waiting on a late Constancia sits in. Distinct from "nothing to remit" on a month
   *  that was never filed at all, which means something else entirely. */
  readonly awaitingMoreRetentions = computed(
    () => this.hasFiledThisPeriod() && this.canRemit() && this.totalToRemit() <= 0 && !this.hasDraft(),
  );

  /** What actually leaves the bank: the withholdings plus whatever SAR charged. */
  readonly totalPaid = computed(() => (this.draft()?.amount ?? this.totalToRemit()) + this.chargesTotal());

  constructor() {
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    for (let m = 1; m <= 12; m++) this.monthOptions.push({ label: this.monthName(m), value: m });
  }

  ngOnInit(): void {
    // Default to the previous month — the one due by the 10th of the current month. A caller that knows the
    // period (a cash request's usage list linking to the remittance that spent its money) names it instead,
    // so the link lands on the month it was talking about rather than on today's default.
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const params = this.route.snapshot.queryParamMap;
    this.year.set(Number(params.get('year')) || prev.getFullYear());
    this.month.set(Number(params.get('month')) || prev.getMonth() + 1);
    for (let y = now.getFullYear(); y >= now.getFullYear() - 4; y--) this.yearOptions.push({ label: `${y}`, value: y });

    // Mirror the two controls the conversion readouts depend on. A changed account can turn a wire that
    // converted into one that does not, so the rate is dropped with it rather than left behind to price a
    // conversion nobody is making any more.
    this.bankControl.valueChanges.subscribe((id) => {
      this.selectedBankId.set(id);
      if (!this.converts()) {
        this.rateControl.setValue(null, { emitEvent: false });
        this.typedRate.set(null);
      }
    });
    this.rateControl.valueChanges.subscribe((rate) => this.typedRate.set(rate));

    this.loadBanks();
    this.loadCashRequests();
    this.loadWorksheet();
  }

  // ── helpers ─────────────────────────────────────────────────────────────────
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private monthName(m: number): string {
    return new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' });
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  getRow(row: unknown): RemittanceWorksheetRow {
    return row as RemittanceWorksheetRow;
  }

  retentionSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Posted':
        return 'success';
      case 'Draft':
        return 'warn';
      default:
        return 'danger'; // None — no certificate
    }
  }

  // ── viewing a filing already made (#5029) ───────────────────────────────────

  /**
   * Opens a past filing read-only.
   *
   * <p>Fetched in full rather than rendered from the summary already on screen: the summary deliberately
   * carries no lines or charges, and those are the whole reason someone opens a filing — to see WHICH
   * retentions it covered before deciding whether the invoice they just found is really missing from it.</p>
   */
  openRemittance(summary: RemittanceSummary): void {
    if (!summary.id) return;

    this.viewing.set(null);
    this.viewerOpen.set(true);
    this.startLoading();
    this.service
      .getById(summary.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (res) => this.viewing.set(res.data ?? null),
        error: () => this.viewerOpen.set(false),
      });
  }

  closeViewer(): void {
    this.viewerOpen.set(false);
    this.viewing.set(null);
  }

  remittanceSeverity(status: WithholdingRemittanceStatus): 'success' | 'warn' {
    return status === WithholdingRemittanceStatus.Posted ? 'success' : 'warn';
  }

  remittanceStatusLabel(status: WithholdingRemittanceStatus): string {
    return status === WithholdingRemittanceStatus.Posted
      ? this.t('WithholdingRemittance_Status_Posted', 'Remitted')
      : this.t('WithholdingRemittance_Status_Draft', 'Draft');
  }

  // ── charges (#5029) ─────────────────────────────────────────────────────────

  /** Builds one charge row. Description and account are required because the API refuses either missing,
   *  and finding that out at post time — after the confirm dialog — is the wrong place to learn it. */
  private chargeGroup(charge?: WithholdingRemittanceCharge): FormGroup<WithholdingRemittanceChargeFormGroup> {
    return new FormGroup<WithholdingRemittanceChargeFormGroup>({
      description: new FormControl<string | null>(charge?.description ?? null, Validators.required),
      glAccountReference: new FormControl<string | null>(charge?.glAccountReference ?? null, Validators.required),
      glAccountNumber: new FormControl<string | null>(charge?.glAccountNumber ?? null),
      accountName: new FormControl<string | null>(charge?.accountName ?? null),
      amount: new FormControl<number | null>(charge?.amount ?? null, [Validators.required, Validators.min(0.01)]),
    });
  }

  addCharge(): void {
    this.charges.push(this.chargeGroup());
  }

  removeCharge(index: number): void {
    this.charges.removeAt(index);
    this.recomputeChargesTotal();
  }

  /** Re-seeds the rows from a loaded draft, replacing whatever was on screen. */
  private applyCharges(charges: WithholdingRemittanceCharge[]): void {
    this.charges.clear({ emitEvent: false });
    for (const charge of charges) this.charges.push(this.chargeGroup(charge), { emitEvent: false });
    this.recomputeChargesTotal();
  }

  private recomputeChargesTotal(): void {
    this.chargesTotal.set(this.charges.controls.reduce((sum, group) => sum + (group.controls.amount.value ?? 0), 0));
  }

  /** Called from the template on every amount edit — a FormArray's valueChanges does not survive the
   *  clear/push cycle above without re-subscribing, and one explicit call is simpler than managing that. */
  onChargeAmountChange(): void {
    this.recomputeChargesTotal();
  }

  /** The charge rows as the API wants them. Account NUMBER and name are display only and deliberately not
   *  sent: the API re-resolves them from the id, so a stale name on screen can never be persisted. */
  private chargesPayload() {
    return this.charges.controls.map((group) => ({
      description: group.controls.description.value,
      glAccountReference: group.controls.glAccountReference.value,
      amount: group.controls.amount.value ?? 0,
    }));
  }

  /** Saves the charges on their own, so an officer can put the form down and come back to it. */
  saveCharges(): void {
    const draft = this.draft();
    if (!draft?.id || !this.validateCharges()) return;

    this.startLoading();
    this.service
      .saveCharges(draft.id, { charges: this.chargesPayload() })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (res) => {
          this.draft.set(res.data ?? null);
          this.applyCharges(res.data?.charges ?? []);
          this.charges.markAsPristine();
          GlobalMessageUtil.success(
            this.t('WithholdingRemittance_ChargesSaved', 'Saved'),
            this.t('WithholdingRemittance_ChargesSaved_Detail', 'Charges saved on the draft.'),
          );
        },
      });
  }

  /** True when every row is complete. Marks the form touched so the incomplete rows actually show why. */
  private validateCharges(): boolean {
    if (this.charges.valid) return true;

    this.charges.markAllAsTouched();
    GlobalMessageUtil.warn(
      this.t('WithholdingRemittance_ChargeIncomplete', 'Incomplete charge'),
      this.t(
        'WithholdingRemittance_ChargeIncomplete_Detail',
        'Every charge needs a description, a GL account and an amount greater than zero.',
      ),
    );
    return false;
  }

  // ── data ────────────────────────────────────────────────────────────────────
  /**
   * The Approved cash requests this remittance could be drawn from (#4796). Approved only: a week still being
   * drafted has not committed to funding anything, so offering it would invite tagging money to a plan that
   * may never exist.
   */
  private loadCashRequests(): void {
    this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).subscribe({
      next: (companyId) => {
        if (!companyId) return;
        this.cashRequestService.search(companyId, CashRequestStatus.Approved).subscribe({
          next: (response) =>
            this.cashRequestOptions.set(
              (response.data ?? [])
                .filter((request) => !!request.id)
                .map((request) => ({
                  label: `${request.number} · ${new Date(request.weekStart).toLocaleDateString()}`,
                  value: request.id!,
                })),
            ),
          error: () => this.cashRequestOptions.set([]),
        });
      },
      error: () => this.cashRequestOptions.set([]),
    });
  }

  /**
   * What the plan already says, when it says anything: how many of the batch's retentions were budgeted, and
   * whether they agree on a week. Shown beside the selector so the officer can tell a pre-filled answer from
   * their own — and so a month planned across several weeks reads as that rather than as a blank.
   */
  plannedCashRequestHint(): string | null {
    const lines = this.draft()?.lines ?? [];
    if (lines.length === 0) return null;

    const planned = lines.filter((line) => !!line.cashRequest?.reference);
    if (planned.length === 0)
      return this.t('WithholdingRemittance_CashRequest_None', 'None of these retentions was planned in a cash request.');

    const distinct = new Set(planned.map((line) => line.cashRequest!.reference));
    if (distinct.size > 1) {
      return this.t(
        'WithholdingRemittance_CashRequest_Many',
        'These retentions were planned across several cash requests — each keeps its own; this fills the rest.',
      );
    }
    return `${planned.length}/${lines.length} ${this.t('WithholdingRemittance_CashRequest_Planned', 'retentions were planned in this request.')}`;
  }

  private loadBanks(): void {
    this.bankService.getAll().subscribe({
      next: (res) => {
        const accounts = res.data ?? [];
        this.bankAccounts.set(accounts);
        // The currency is part of the account's identity here (#5029): choosing a dollar account for a
        // Lempira filing is a decision about whether the bank converts, so it cannot be invisible in the
        // list the decision is made from.
        this.bankOptions.set(
          accounts.map((b: BankAccount) => ({ label: `${b.name} — ${b.bankName} (${this.currencyLabel(b.currency)})`, value: b.id! })),
        );
      },
      error: () => {
        this.bankAccounts.set([]);
        this.bankOptions.set([]);
      },
    });
  }

  loadWorksheet(): void {
    this.draft.set(null);
    this.applyCharges([]);
    this.startLoading();
    this.service
      .getWorksheet(this.year(), this.month())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (res) => {
          this.worksheet.set(res.data ?? null);
          this.loadOpenDraft();
        },
        error: () => this.worksheet.set(null),
      });
  }

  /**
   * Loads the period's open draft, if it has one (#5029).
   *
   * <p>Without this the page could only see a draft it had created in the current browser session, so
   * reopening the period showed no form, no Prepare button — the API refuses to open a SECOND draft while one
   * is open — and a "this period has already been remitted" message that was doubly wrong: nothing had been
   * remitted, and the draft that existed was unreachable.</p>
   */
  private loadOpenDraft(): void {
    const openDraft = this.worksheet()?.remittances?.find((r) => r.status === WithholdingRemittanceStatus.Draft);
    if (!openDraft?.id) return;

    this.startLoading();
    this.service
      .getById(openDraft.id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (res) => {
          this.draft.set(res.data ?? null);
          this.applyCharges(res.data?.charges ?? []);
          this.charges.markAsPristine();
          this.cashRequestControl.setValue(res.data?.cashRequest?.reference ?? null);
        },
        // A draft that will not load must not blank the page; the worksheet is still worth reading.
        error: () => undefined,
      });
  }

  onPeriodChange(): void {
    this.loadWorksheet();
  }

  prepare(): void {
    this.startLoading();
    this.service
      .build(this.year(), this.month())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (res) => {
          this.draft.set(res.data ?? null);
          // A rebuilt draft keeps whatever charges were already entered — they are a person's input, not
          // derived from the period's retentions — so re-seed the rows rather than blanking them.
          this.applyCharges(res.data?.charges ?? []);
          // The API pre-fills the header when every planned retention agrees on one week — take that as the
          // default rather than making the officer re-answer what the plan already decided.
          this.cashRequestControl.setValue(res.data?.cashRequest?.reference ?? null);
          GlobalMessageUtil.success(
            this.t('WithholdingRemittance_Prepared', 'Prepared'),
            this.t('WithholdingRemittance_Prepared_Detail', 'Remittance prepared — select a bank account and post.'),
          );
        },
      });
  }

  /**
   * Re-seeds the open draft from the period (#5029) — the path for "I certified another retention after
   * preparing this". `prepare()` already rebuilds an existing draft rather than creating a second, so this
   * is the same call; it needs its own entry point only because the Prepare button is hidden once a draft
   * exists.
   *
   * <p>Refused while charges are unsaved: rebuilding replaces the rows from the server, and silently
   * discarding a penalty someone just typed is not a trade worth making for one fewer click.</p>
   */
  refreshDraft(): void {
    if (this.charges.dirty) {
      GlobalMessageUtil.warn(
        this.t('WithholdingRemittance_SaveChargesFirst', 'Unsaved charges'),
        this.t(
          'WithholdingRemittance_SaveChargesFirst_Detail',
          'Save the charges before refreshing — refreshing reloads the remittance from the server and would discard them.',
        ),
      );
      return;
    }
    this.prepare();
  }

  remit(): void {
    const draft = this.draft();
    if (!draft?.id) return;
    if (!this.bankControl.value) {
      GlobalMessageUtil.warn(
        this.t('WithholdingRemittance_BankRequired', 'Bank account required'),
        this.t('WithholdingRemittance_BankRequired_Detail', 'Select the bank account to pay SAR from.'),
      );
      return;
    }
    if (!this.validateCharges()) return;
    // The bank converts and nobody has said at what (#5029). Refused here rather than at the API so the
    // officer is told with the field in front of them, and because the confirmation below would otherwise
    // quote a cash figure the screen cannot yet work out.
    if (this.converts() && !((this.rateControl.value ?? 0) > 0)) {
      this.rateControl.markAsTouched();
      GlobalMessageUtil.warn(
        this.t('WithholdingRemittance_RateRequired', 'Exchange rate required'),
        this.t(
          'WithholdingRemittance_RateRequired_Detail',
          'This filing is paid from an account in another currency. Enter the exchange rate the bank gave you.',
        ),
      );
      return;
    }
    if (this.rateOutOfBounds()) {
      this.rateControl.markAsTouched();
      GlobalMessageUtil.warn(
        this.t('WithholdingRemittance_RateOutOfBounds', 'Check the exchange rate'),
        this.t('WithholdingRemittance_RateOutOfBounds_Detail', 'That rate is outside the range this currency trades at.'),
      );
      return;
    }
    this.confirmationService.confirm({
      header: this.t('WithholdingRemittance_Confirm_Header', 'Remit to SAR'),
      message: this.t(
        'WithholdingRemittance_Confirm_Message',
        'This posts Dr Retenciones por Pagar / Cr Bank and marks the withholdings as remitted. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doRemit(draft.id!),
    });
  }

  private doRemit(id: string): void {
    this.startLoading();
    // Unsaved charges are saved before posting, so the total the officer just confirmed is the total that
    // posts. Leaving them to be saved separately meant typing a penalty, hitting Remit, and having it
    // silently not be there — the amount on screen and the amount paid have to be the same number.
    const saveFirst: Observable<unknown> = this.charges.dirty ? this.service.saveCharges(id, { charges: this.chargesPayload() }) : of(null);

    saveFirst
      .pipe(
        switchMap(() =>
          this.service.post(id, {
            bankAccountReference: this.bankControl.value,
            bankTransactionNumber: this.bankingRefControl.value,
            // Only when the bank actually converts (#5029) — sending a rate for a wire that converts
            // nothing would be asserting a conversion that did not happen.
            exchangeRateToUsd: this.converts() ? this.rateControl.value : null,
            cashRequestReference: this.cashRequestControl.value,
            notes: this.notesControl.value,
          }),
        ),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.t('WithholdingRemittance_Remitted', 'Remitted'),
            this.t('WithholdingRemittance_Remitted_Detail', 'Withholdings remitted to SAR.'),
          );
          this.bankControl.reset();
          this.bankingRefControl.reset();
          this.rateControl.reset();
          this.typedRate.set(null);
          this.cashRequestControl.reset();
          this.notesControl.reset();
          this.applyCharges([]);
          this.loadWorksheet();
        },
      });
  }

  back(): void {
    this.router.navigate(['/ap-withholdings']);
  }
}
